import LogRocket from 'logrocket';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STRIPE_TERMINAL_LOCATION_ID } from '../../../config';
import {
  useOnce,
  useUserStateRedux,
  useFeatureFlagRedux,
} from '../../../hooks';
import useActiveProvider from '../../../hooks/useActiveProvider';
import analytics from '../../../modules/analytics';
import { isSimulated } from '../../../modules/TapToPay';
import { useTapToPayReaderConnection } from '../hooks/useTapToPayReaderConnection';
import { onAppPause } from '../../../modules/onAppPause';
import { onAppResume } from '../../../modules/onAppResume';
import store from '../../../store';
import type { IRootDispatch, RootState } from '../../../store/models';
import { TAP_TO_PAY_FEATURE_FLAG, HAD_SUCCESSFUL_CONNECTION_USERSTATE_KEY } from '../checkout/TapToPayFlow/constants';
import { log } from '../../../modules/TapToPay/TapToPayLogger';

enum AnalyticsEvent {
  ProOptedIn = 'Init Tap2Pay Pro Opted In',
}

export const StripeTerminalConnector: React.FC = () => {
  const dispatch = useDispatch<IRootDispatch>();
  const provider = useActiveProvider();
  const isEligibleProvider = useSelector(
    (state: RootState) => !state.user?.is_anon
      && !state.user?.is_superuser
      && !!state.user?.providerId,
  );
  const isPluginLoaded = useSelector(
    store.select.stripeTerminal.isPluginLoaded,
  );

  /** Did pro connect successfully previously on this or another device? */
  const [
    hadSuccessfulConnection,
    setHadSuccessfulConnection,
    hadSuccessfulConnectionLoaded,
  ] = useUserStateRedux<boolean>(HAD_SUCCESSFUL_CONNECTION_USERSTATE_KEY);

  const optedIn = React.useMemo(
    () => (hadSuccessfulConnectionLoaded && hadSuccessfulConnection),
    [
      hadSuccessfulConnectionLoaded,
      hadSuccessfulConnection,
    ],
  );

  React.useEffect(() => {
    if (!hadSuccessfulConnectionLoaded) return;
    if (optedIn) {
      log('StripeTerminalConnector: Pro had prior connection');
      analytics.track(AnalyticsEvent.ProOptedIn);
    } else {
      log('StripeTerminalConnector: Pro did not have prior connection');
    }
  }, [
    hadSuccessfulConnectionLoaded,
    optedIn,
  ]);

  const tapToPayFeatureFlag = useFeatureFlagRedux(TAP_TO_PAY_FEATURE_FLAG);
  const shouldInitPlugin = React.useMemo(
    () => {
      if (isPluginLoaded
        || !window.cordovaTapToPay
        || tapToPayFeatureFlag.loading
      ) {
        return false;
      }
      return tapToPayFeatureFlag.enabled || false;
    },
    [
      isPluginLoaded,
      tapToPayFeatureFlag,
    ],
  );
  useOnce(() => {
    log('Initializing Plugin');
    dispatch.stripeTerminal.init();
  }, shouldInitPlugin || null);

  const handleConnectSuccess = React.useCallback(() => {
    setHadSuccessfulConnection(true);
  }, [setHadSuccessfulConnection]);

  const {
    initReaderConnection,
    killReaderConnection,
  } = useTapToPayReaderConnection({
    locationId: STRIPE_TERMINAL_LOCATION_ID,
    merchantDisplayName: provider?.name,
    providerId: provider?.id,
    getState: store.getState,
    onConnectSuccess: handleConnectSuccess,
  }, {
    isSimulated: isSimulated(),
    installReconnectHandler: true,
  });

  const shouldInitReaderConnection = React.useMemo(
    () => (isEligibleProvider && provider?.can_process_payments && optedIn),
    [
      isEligibleProvider,
      provider?.can_process_payments,
      optedIn,
    ],
  );
  /** This is a useEffect and not a useOnce because we need to support multiple login sessions.
   */
  React.useEffect(
    () => {
      if (!isPluginLoaded) return;

      if (shouldInitReaderConnection) {
        initReaderConnection();
      } else {
        killReaderConnection();
      }
    },
    /** ** Important note: Every dependency used in this effect must be memoized **** */
    [
      initReaderConnection,
      killReaderConnection,
      shouldInitReaderConnection,
      isPluginLoaded,
    ],
  );

  React.useEffect(() => onAppResume(() => {
    log('didAppResume');
    LogRocket.track('App Resumed');
  }), []);

  React.useEffect(() => onAppPause(() => {
    log('didAppPause');
    LogRocket.track('App Paused');
  }), []);

  return null;
};
